export const QueryKeys = {
    announcements: {
        settings: (orgID) => ['announcements', orgID, 'settings'],
        all: (orgID) => ['announcements', orgID, 'all'],
        report: (orgID, id) => ['announcements', orgID, 'report', id],
    },
    guides: {
        state: (accountID) => ['guides', accountID],
    },
    assistant: {
        sessions: (organizationID, personID) => ['assistant', organizationID, personID, 'sessions'],
        messages: (organizationID, personID, sessionID) => ['assistant', organizationID, personID, 'messages', sessionID],
    },
    importAssociations: {
        all: () => ['importAssociations'],
    },
    importTemplates: {
        all: () => ['importTemplates'],
    },
    global: {
        all: (search_term, types, match_all, partial) => ['globalSearch', search_term, match_all, partial, types],
    },
    kpis: {
        attendanceUnique: ({startDate, endDate}) => ['attendanceUniqueKPIs', {startDate, endDate}],
        attendanceUniqueDetail: ({startDate, endDate}) => ['attendanceUniqueDetail', {startDate, endDate}],
        attendanceFirstFouthTimeVisitors: ({startDate, endDate}) => ['attendanceFirstFouthTimeVisitorsKPIs', {startDate, endDate}],
        attendanceFirstFouthTimeVisitorsDetail: ({startDate, endDate}) => ['attendanceFirstFouthTimeVisitorsDetail', {startDate, endDate}],
        attendanceDateFields: ({startDate, endDate, dateFieldID, countOnly}) => ['attendanceDateFieldsKPIs', {startDate, endDate, dateFieldID, countOnly}],
        attendanceWeeklyUniqueAvg: ({startDate, endDate}) => ['attendanceWeeklyUniqueAvgKPIs', {startDate, endDate}, 'tracked-v2'],
    },
    reports: {
        missed: (days, eventID, filters, searchText) => ['missedReport', days, eventID, filters, searchText],
        birthdays: (days, filters, searchText) => ['birthdayReport', days, filters, searchText],
        volunteerUtilization: ({startDate, endDate, dueTypes}) => ['volunteerUtilization', {startDate, endDate, dueTypes}],
    },
    organization: {
        all: () => ['organizations'],
        byId: (id) => ['organizations', id],
        byUserID: (userID) => ['organizations', {userID}],
    },
    organizationEntitlements: {
        // Stable 'current' slot: entitlements only ever describe the session's
        // active org, and keying by getCachedOrgID() created TWO queries
        // ('current' vs the org id) that observers ping-ponged between —
        // the split made entitlements flip between payload and null, which
        // churned tab config and remounted the tab group every fetch cycle.
        // Org switches go through clearOrgIDCache + invalidation instead.
        current: () => ['organizationEntitlements', 'current'],
    },
    registrations: {
        all: () => ['registrations'],
        byRequestingUser: (requestingUserID) => ['registrations', {requestingUserID}],
    },
    registeredUsers: {
        all: () => ['registeredUsers'],
    },
    invites: {
        all: () => ['invites'],
        forUser: (email) => ['invites', { email }],
    },
    people: {
        all: ({searchTerm, filters, filterLogic, sortKey, sortDirection} = {}) => !searchTerm && !filters && !filterLogic && !sortKey && !sortDirection ? ['people'] : ['people', {searchTerm, filters, filterLogic, sortKey, sortDirection}],
        recentAttendance: (eventID, searchTerm, eventInstanceID) => searchTerm || eventInstanceID ? ['recentAttendance', eventID, searchTerm, eventInstanceID] : ['recentAttendance', eventID],
        attendanceHistory: (personID, eventName = null, startDate, endDate) => ['attendanceHistory', personID, eventName, startDate, endDate],
        infinite: () => ['people', {infinite: true}],
        // plan marker segregates cached profiles across a plan change: a paid
        // profile (with composed customFieldValues) must never serve a Free
        // render, and invalidateQueries(['people', id]) still prefix-matches.
        byId: (id, plan) => ['people', id, ...(plan ? [`plan:${plan}`] : [])],
        basicById: (id) => ['people', 'basic', id],
        byIdForMerge: (id, plan) => ['people', id, 'forMerge', ...(plan ? [`plan:${plan}`] : [])],
        potentialDuplicates: () => ['people', 'potentialDuplicates'],
        byName: (firstName, lastName) => ['people', 'byName', firstName?.toLowerCase(), lastName?.toLowerCase()],
        loggedInUser: () => ['loggedInUser', 'withAccountEmail'],
        emulatingUser: () => ['emulatingUser'],
        withConversationAccess: (orgID) => ['people', 'withConversationAccess', orgID],
    },
    devices: {
        all: () => ['devices'],
    },
    apiKeys: {
        all: () => ['apiKeys'],
    },
    quickFilters: {
        all: () => ['quickFilters']  
    },
    profileViews: {
        all: () => ['profileViews'],
        byId: (id) => ['profileViews', id],
        // Config serialized into the key so unsaved-draft previews refetch;
        // 'draft' marks the p_view_id:null preview path.
        card: (viewID, personID, config) => ['profileViews', 'card', viewID ?? 'draft', personID, config ? JSON.stringify(config) : null],
    },
    personCustomFieldSections: {
        all: () => ['personCustomFieldSections'],
        byId: (id) => ['personCustomFieldSections', id],
    },
    eventCustomFieldSections: {
        all: () => ['eventCustomFieldSections'],
        byId: (id) => ['eventCustomFieldSections', id],
    },
    personCustomFields: {
        all: () => ['personCustomFields'],
        byId: (id) => ['personCustomFields', id],
    },
    protectedPersonFields: {
        all: () => ['protectedPersonFields'],
    },
    eventCustomFields: {
        all: () => ['eventCustomFields'],
    },
    customFieldSections: {
        all: (module) => ['customFieldSections', module],
        byId: (id) => ['customFieldSections', id],
    },
    customFields: {
        all: (module) => ['customFields', module],
        byId: (id) => ['customFields', id],
    },
    addresses: {
        all: () => ['addresses'],
        byId: (id) => ['addresses', id],
    },
    events:{
        all: () => ['events'],
        byId: (id) => ['events', id],
        byIdForMerge: (id) => ['events', id, 'forMerge'],
        analytics: (eventID, numberOfDays) => ['events', eventID, numberOfDays, 'analytics'],
        uniqueAttendees: (eventID, numberOfDays) => ['events', eventID, numberOfDays, 'uniqueAttendees'],
        uniqueAttendeesDetail: (eventID, numberOfDays) => ['events', eventID, numberOfDays, 'uniqueAttendees', 'detail'],
        picker: (recentOnly) => ['events', 'picker', {recentOnly}],
        publicJesusTentRevivalStats: () => ['events', 'public', 'jesusTentRevivalStats'],
    },
    blackoutDates: {
        all: () => ['blackoutDates'],
        byMonth: (month, year, personID) => ['blackoutDates', {month, year, personID}],
    },
    eventInstances:{
        all: () => ['eventInstances'],
        forDate: (dateKey) => ['eventInstances', 'forDate', dateKey],
        byId: (id) => ['eventInstances', id],
        serviceFlow: (eventInstanceID) => ['eventInstances', eventInstanceID, 'serviceFlow'],
        attendance: (eventInstanceID) => ['attendance', eventInstanceID],
        registrants: (eventInstanceID) => ['eventInstances', 'registrants', eventInstanceID],
        registrantCodes: (eventInstanceID, personID) => ['eventInstances', eventInstanceID, 'registrants', personID, 'codes'],
        registrantPaths: (eventInstanceID) => ['eventInstances', 'registrantPaths', eventInstanceID],
        registrantPathEnrollments: (eventInstanceID, personID) => ['eventInstances', 'registrantPathEnrollments', eventInstanceID, personID],
        // attendanceEventInstanceID (check-in target, may differ from the
        // registration-owning instance in an extension set) appended only when
        // provided — keeps 4-part keys valid as invalidation prefixes.
        registrantDetail: (eventInstanceID, personID, attendanceEventInstanceID) => ['eventInstances', 'registrantDetail', eventInstanceID, personID, ...(attendanceEventInstanceID ? [attendanceEventInstanceID] : [])],
        pendingRegistrants: (eventInstanceID) => ['eventInstances', 'pendingRegistrants', eventInstanceID],
        byEventId: (eventID) => ['eventInstances', 'past', eventID],
        byEventIdAll: (eventID) => ['eventInstances', 'all', eventID],
        upcomingVolunteerTasks: (eventID) => ['eventInstances', 'upcomingVolunteerTasks', eventID],
        forRegistration: () => ['eventInstances', 'forRegistration'],
        registrationExtensions: (eventInstanceID) => ['eventInstances', 'registrationExtensions', eventInstanceID],
        registrationExtensionSource: (eventInstanceID) => ['eventInstances', 'registrationExtensionSource', eventInstanceID],
    },
    locations: {
        all: () => ['locations'],
        byId: (id) => ['locations', id],
        scheduleForDay: (dayKey) => ['locations', 'schedule', dayKey],
    },
    calendars: {
        all: () => ['calendars'],
        byId: (id) => ['calendars', id],
    },
    boards: {
        all: () => ['boards'],
        byId: (id) => ['boards', id],
        orgAll: () => ['boards', {orgAll: true}],
        members: (boardID) => ['boards', boardID, 'members'],
    },
    posts: {
        byId: (id) => ['posts', id],
        byBoardId: (id) => ['posts', {boardID: id}],
        myBoardsFeed: () => ['posts', 'myBoardsFeed'],
        viewers: (id) => ['posts', id, 'viewers'],
    },
    comments: {
        byPostId: (id) => ['comments', {postID: id}]
    },
    conversations: {
        all: () => ['conversations'],
        byUserId: (id) => ['conversations', {userId: id}],
        byId: (id) => ['conversations', id],
        orgAll: () => ['conversations', {orgAll: true}],
    },
    messages: {
        byConversationId: (id) => ['messages', {conversationID: id}]
    },
    tasks: {
        all: () => ['tasks'],
        infinite: () => ['tasks', {infinite: true}],
        byId: (id) => ['tasks', id],
        byDueType: (type) => ['tasks', type],
        upcomingTasks: (personID, horizonDays) => personID == null ? ['upcomingTasks'] : horizonDays == null ? ['upcomingTasks', personID] : ['upcomingTasks', personID, horizonDays],
        upcomingTaskAssignments: (personID, horizonDays) => personID == null ? ['upcomingTaskAssignments'] : horizonDays == null ? ['upcomingTaskAssignments', personID] : ['upcomingTaskAssignments', personID, horizonDays],
        upcomingTeamTaskInstances: (teamID, horizonDays) => teamID == null ? ['upcomingTeamTaskInstances'] : horizonDays == null ? ['upcomingTeamTaskInstances', teamID] : ['upcomingTeamTaskInstances', teamID, horizonDays],
        personHistory: (personID) => ['tasks', 'personHistory', personID],
        recentAssignees: (taskID) => ['tasks', 'recentAssignees', taskID],
        recentAssigneesBatch: (taskIDs) => ['tasks', 'recentAssigneesBatch', taskIDs?.slice().sort().join(',')],
    },
    taskInstances: {
        byId: (id) => ['taskInstances', id],
        individual: () => ['taskInstances', 'individual'],
    },
    taskAutoSchedule: {
        // Scoped by org AND person so an org switch or sign-out can never
        // serve another actor's draft. Root 'taskAutoSchedule' is deliberately
        // NOT in PERSISTABLE_KEYS — draft payloads never reach AsyncStorage.
        all: (orgID, personID) => ['taskAutoSchedule', orgID, personID],
        preview: (orgID, personID, runID) => ['taskAutoSchedule', orgID, personID, 'preview', runID],
        exclusions: (orgID, personID, runID) => ['taskAutoSchedule', orgID, personID, 'exclusions', runID],
    },
    taskTeams: {
        all: () => ['taskTeams'],
        byId: (id) => ['taskTeams', id],
    },
    taskAssignmentTemplates: {
        all: () => ['taskAssignmentTemplates'],
        byId: (id) => ['taskAssignmentTemplates', id],
    },
    roles: {
        all: () => ['roles'],
        byId: (id) => ['roles', id],
    },
    roleFolders: {
        all: () => ['roleFolders'],
        byId: (id) => ['roleFolders', id],
    },
    permissions: {
        all: () => ['permissions'],
        byId: (id) => ['permissions', id],
        infinite: () => ['permissions', {infinite: true}],
    },
    songs: {
        all: () => ['songs'],
        byId: (id) => ['songs', id],
    },
    smallGroupSessions: {
        byId: (id) => ['smallGroupSessions', id],
    },
    smallGroups: {
        all: ({searchTerm, filters, sortKey, sortDirection} = {}) => ['smallGroups', {searchTerm, filters, sortKey, sortDirection}],
        forDropdown: (smallGroupTypeID = null) => ['smallGroups', 'dropdown', smallGroupTypeID],
        byId: (id) => ['smallGroups', id],
        metrics: ({smallGroupTypeID, startDate, endDate}) => ['smallGroups', 'metrics', smallGroupTypeID, startDate, endDate],
        trends: ({smallGroupTypeID, startDate, endDate}) => ['smallGroups', 'trends', smallGroupTypeID, startDate, endDate],
        reportingStats: (smallGroupTypeID) => ['smallGroups', 'reportingStats', smallGroupTypeID],
        dateStats: ({smallGroupTypeID, startDate, endDate}) => ['smallGroups', 'dateStats', smallGroupTypeID, startDate, endDate],
        teachersDetail: ({smallGroupTypeID, startDate, endDate}) => ['smallGroups', 'teachersDetail', smallGroupTypeID, startDate, endDate],
        studentsDetail: ({smallGroupTypeID, startDate, endDate}) => ['smallGroups', 'studentsDetail', smallGroupTypeID, startDate, endDate],
        activeGroupsDetail: ({smallGroupTypeID}) => ['smallGroups', 'activeGroupsDetail', smallGroupTypeID],
        activeLeadersDetail: ({smallGroupTypeID}) => ['smallGroups', 'activeLeadersDetail', smallGroupTypeID],
        activeStudentsDetail: ({smallGroupTypeID}) => ['smallGroups', 'activeStudentsDetail', smallGroupTypeID],
        sessionsDetail: ({smallGroupTypeID, startDate, endDate}) => ['smallGroups', 'sessionsDetail', smallGroupTypeID, startDate, endDate],
        newGroupsDetail: ({smallGroupTypeID, startDate, endDate}) => ['smallGroups', 'newGroupsDetail', smallGroupTypeID, startDate, endDate],
        completedGroupsDetail: ({smallGroupTypeID, startDate, endDate}) => ['smallGroups', 'completedGroupsDetail', smallGroupTypeID, startDate, endDate],
        leaderReport: ({smallGroupTypeID, startDate, endDate, includeWithoutMeetings}) => ['smallGroups', 'leaderReport', smallGroupTypeID, startDate, endDate, includeWithoutMeetings],
        sessionsPaginated: ({smallGroupTypeID, startDate, endDate, smallGroupID, teacherID, searchTerm, sortKey, sortDirection}) => ['smallGroups', 'sessions', {smallGroupTypeID, startDate, endDate, smallGroupID, teacherID, searchTerm, sortKey, sortDirection}],
        byIdForMerge: (id) => ['smallGroups', id, 'forMerge'],
    },
    smallGroupTypes: {
        all: () => ['smallGroupTypes'],
        byId: (id) => ['smallGroupTypes', id],
    },
    bibleStudies: {
        all: ({scope, status, searchTerm, teacherID, sortKey, sortDirection} = {}) => ['bibleStudies', {scope, status, searchTerm, teacherID, sortKey, sortDirection}],
        forDropdown: (smallGroupTypeID = null) => ['bibleStudies', 'dropdown', smallGroupTypeID],
        sessions: ({scope, smallGroupID, startDate, endDate, teacherID, searchTerm, sortKey, sortDirection} = {}) => ['bibleStudies', 'sessions', {scope, smallGroupID, startDate, endDate, teacherID, searchTerm, sortKey, sortDirection}],
        sessionNotes: (smallGroupID) => ['bibleStudies', 'sessionNotes', smallGroupID],
        students: (smallGroupID) => ['bibleStudies', 'students', smallGroupID],
        peopleNotes: (smallGroupID) => ['bibleStudies', 'peopleNotes', smallGroupID],
        teacherDetail: ({scope, teacherID, startDate, endDate} = {}) => ['bibleStudies', 'teacherDetail', {scope, teacherID, startDate, endDate}],
        studentOutcomes: ({scope, teacherID, startDate, endDate, windowDays} = {}) => ['bibleStudies', 'studentOutcomes', {scope, teacherID, startDate, endDate, windowDays}],
    },
    links: {
        all: () => ['links'],
        byId: (id) => ['links', id],
    },
    activity: {
        all: () => ['activity'],
        feed: ({ actorID, subjectType, subjectID, verb, dateFrom, dateTo, search }) => 
            ['activity', 'feed', { actorID, subjectType, subjectID, verb, dateFrom, dateTo, search }],
        byActor: (actorID) => ['activity', 'byActor', actorID],
        bySubject: (subjectType, subjectID) => ['activity', 'bySubject', subjectType, subjectID],
    },
    notificationTypes: {
        all: () => ['notificationTypes'],
    },
    gifs: {
        all: (searchTerm) => ['gifs', {searchTerm}],
    },
    printLabels: {
        all: () => ['printLabels'],
    },
    contributionBatches: {
        all: () => ['contributions', 'batches'],
        byId: (id) => ['contributions', 'batches', {id}],
        latest: () => ['contributions', 'batches', 'latest'],
        transactions: (batchID, searchText, sortConfig) => ['contributions', 'batches', batchID, 'transactions', {searchText, sortConfig}],
        stats: (batchID) => ['contributions', 'batches', batchID, 'stats'],
    },
    contributionFunds: {
        all: () => ['contributions', 'funds'],
        byId: (id) => ['contributions', 'funds', id],
    },
    contributionMethods: {
        all: () => ['contributions', 'methods'],
        byId: (id) => ['contributions', 'methods', id],
    },
    contributionTransactions: {
        byId: (id) => ['contributions', 'transactions', id],
        byPersonId: ({ personID, personIDs, startDate, endDate, fundID, methodID, search }) => ['contributions', 'transactions', 'person', { personID, personIDs, startDate, endDate, fundID, methodID, search }],
    },
    contributionAnalytics: {
        transactions: ({ startDate, endDate, batchID, giverID, methodIds, amount }) => ['contributions', 'analytics', 'transactions', { startDate, endDate, batchID, giverID, methodIds, amount }],
        kpis: ({ startDate, endDate }) => ['contributions', 'analytics', 'kpis', { startDate, endDate }],
        byMonth: ({ startDate, endDate }) => ['contributions', 'analytics', 'byMonth', { startDate, endDate }],
        topGivers: ({ startDate, endDate, limit }) => ['contributions', 'analytics', 'topGivers', { startDate, endDate, limit }],
        byMethod: ({ startDate, endDate }) => ['contributions', 'analytics', 'byMethod', { startDate, endDate }],
        byFund: ({ startDate, endDate }) => ['contributions', 'analytics', 'byFund', { startDate, endDate }],
        availableYears: () => ['contributions', 'analytics', 'availableYears'],
    },
    shortcuts: {
        byPersonId: (personID) => ['shortcuts', personID],
        recentByPersonId: (personID) => ['shortcuts', 'recent', personID],
    },
    dashboardSections: {
        get: (organizationID, accountID) => organizationID
            ? ['dashboardSections', organizationID, accountID] : ['dashboardSections'],
    },
    organizationSetup: {
        state: (organizationID, accountID) => ['organizationSetup', organizationID, accountID],
    },
    dashboardData: {
        byPersonId: (personID) => ['dashboardData', personID],
    },
    quickPersonFields: {
        get: () => ['quickPersonFields'],
    },
    trainingModules: {
        all: ({ searchTerm, filters, sortKey, sortDirection } = {}) => ['trainingModules', { searchTerm, filters, sortKey, sortDirection }],
        byId: (id) => ['trainingModules', id],
        stats: (moduleID) => ['trainingModules', 'stats', moduleID],
    },
    trainingClassInstances: {
        byId: (id) => ['trainingClassInstances', id],
        byTaskInstanceId: (taskInstanceID) => ['trainingClassInstances', 'byTaskInstanceId', taskInstanceID],
    },
    trainingTerms: {
        byModuleId: (moduleID) => ['trainingTerms', moduleID],
    },
    trainingOverview: {
        stats: () => ['trainingOverview', 'stats'],
    },
    trainingProgress: {
        byPersonId: (personID) => ['trainingProgress', personID],
    },
    upcomingTrainingClasses: {
        byPersonId: (personID) => ['upcomingTrainingClasses', personID],
    },
    paths: {
        all: () => ['paths'],
        byId: (id) => ['paths', id],
        forFormTrigger: () => ['paths', 'formTrigger'],
        forEventCheckInTrigger: () => ['paths', 'eventCheckInTrigger'],
        eventsForCheckInPath: (pathID) => ['paths', 'eventCheckInPath', pathID],
        forManualTrigger: () => ['paths', 'manualTrigger'],
        enrollmentContext: (taskInstanceID) => ['paths', 'enrollmentContext', taskInstanceID],
        variables: (triggerType, formID) => ['pathVariables', triggerType, formID],
    },
    // P9 — schemaVersion-2 Automation surfaces. The 'automations' root holds
    // only v2 contract data (registry, catalog); definition rows themselves
    // stay under 'paths' so every existing invalidation keeps working.
    automations: {
        triggerCatalog: () => ['automations', 'triggerCatalog'],
        variables: (pathID) => ['automations', 'variables', pathID],
    },
    forms: {
        hasEditable: (personID, organizationID) => ['forms', 'hasEditable', personID, organizationID],
        all: () => ['forms'],
        byId: (id) => ['forms', id],
        triggeringPath: (pathID) => ['forms', 'triggeringPath', pathID],
    },
    formFolders: {
        all: () => ['formFolders'],
    },
    formEntries: {
        byFormId: (formID) => ['formEntries', formID],
        byId: (id) => ['formEntries', id],
        mine: (params) => params
            ? ['formEntries', 'mine', { entryID: params.entryID ?? null, limit: params.limit ?? null }]
            : ['formEntries', 'mine'],
    },
    pathEnrollments: {
        byPathId: (pathID) => ['pathEnrollments', { pathID }],
        byPersonId: (personID) => ['pathEnrollments', { personID }],
    },
    emailTemplates: {
        all: () => ['emailTemplates'],
        byId: (id) => ['emailTemplates', id],
    },
    smsCampaigns: {
        all: () => ['smsCampaigns'],
        recipients: (campaignID) => ['smsCampaigns', 'recipients', campaignID],
    },
    emailCampaigns: {
        all: () => ['emailCampaigns'],
        recipients: (campaignID) => ['emailCampaigns', 'recipients', campaignID],
        delivery: (campaignID) => ['emailCampaigns', 'delivery', campaignID],
    },
    files: {
        contents: ({ scope, ownerID, folderID }) => ['files', 'contents', { scope, ownerID, folderID }],
        rootFolder: ({ scope, ownerID }) => ['files', 'rootFolder', { scope, ownerID }],
        tree: ({ scope, ownerID }) => ['files', 'tree', { scope, ownerID }],
        breadcrumbs: ({ folderID }) => ['files', 'breadcrumbs', { folderID }],
        search: ({ query, scope, ownerID, rootFolderID }) => ['files', 'search', { query, scope, ownerID, rootFolderID }],
        storageUsage: () => ['files', 'storageUsage'],
    },
    peopleNotes: {
        byPersonId: (personID) => ['peopleNotes', { personID }],
    },
    maintenance: {
        status: () => ['maintenance', 'status'],
    },
    giving: {
        merchant: (organizationID) => ['giving', 'merchant', organizationID],
        paymentMethods: (organizationID, personID) => ['giving', 'paymentMethods', organizationID, personID],
        recurringGifts: (organizationID, personID) => ['giving', 'recurringGifts', organizationID, personID],
        orgRecurringGifts: (organizationID) => ['giving', 'orgRecurringGifts', organizationID],
        reportSession: (organizationID, reportType) => ['giving', 'reportSession', organizationID, reportType],
    },
    billing: {
        info: () => ['billing', 'info'],
    },
    serviceFlows: {
        all: () => ['serviceFlows'],
        // Legacy v1 keys — kept for the read-only pre-cutover rendering path.
        byEvent: (eventID) => ['serviceFlows', 'event', eventID],
        byInstance: (eventInstanceID) => ['serviceFlows', 'instance', eventInstanceID],
        // v2 keys are scoped by organization AND account (spec §12.5) so no
        // user can receive another account's cached flows.
        scoped: (organizationID, accountID) => ['serviceFlows', 'v2', organizationID, accountID],
        templates: (organizationID, accountID, { archived = false, search = '', page = 0 } = {}) =>
            ['serviceFlows', 'v2', organizationID, accountID, 'templates', { archived, search, page }],
        template: (organizationID, accountID, templateID) =>
            ['serviceFlows', 'v2', organizationID, accountID, 'template', templateID],
        event: (organizationID, accountID, eventID) =>
            ['serviceFlows', 'v2', organizationID, accountID, 'event', eventID],
        instance: (organizationID, accountID, eventInstanceID, locationID) =>
            ['serviceFlows', 'v2', organizationID, accountID, 'instance', eventInstanceID, ...(locationID === undefined ? [] : [locationID])],
    },
    followUp: {
        // Care data is org-scoped so an org switch can never serve another
        // organization's records. Root 'followUp' is deliberately NOT in
        // PERSISTABLE_KEYS or FREE_SAFE_QUERY_ROOTS (spec §8.5): care data
        // never reaches AsyncStorage and is purged on any Free downgrade.
        person: (organizationID, personID) => ['followUp', organizationID, 'person', personID],
        myPeople: (organizationID, caregiverPersonID) => ['followUp', organizationID, 'myPeople', caregiverPersonID],
        personNotes: (organizationID, personID) => ['followUp', organizationID, 'personNotes', personID],
        personTasks: (organizationID, personID) => ['followUp', organizationID, 'personTasks', personID],
        assignedTask: (organizationID, taskInstanceID) => ['followUp', organizationID, 'assignedTask', taskInstanceID],
        needsOwner: (organizationID) => ['followUp', organizationID, 'needsOwner'],
        // P5 — monitoring + attention surfaces (spec §6.1).
        overview: (organizationID) => ['followUp', organizationID, 'overview'],
        // filterKey is a stable serialization of the server-side filter so
        // different filter combinations never share a page cache.
        people: (organizationID, filterKey = 'all') => ['followUp', organizationID, 'people', filterKey],
        my: (organizationID, personID) => ['followUp', organizationID, 'my', personID],
        audiences: (organizationID) => ['followUp', organizationID, 'audiences'],
        // P6 — structured Paths (spec §12/§15.2). The 'paths' prefix covers
        // both the template list and per-template details so a publish
        // invalidates every cached view of the definition at once.
        paths: (organizationID) => ['followUp', organizationID, 'paths'],
        path: (organizationID, pathID) => ['followUp', organizationID, 'paths', pathID],
        // filterKey is a stable serialization of the server-side filter
        // ({personID?, pathID?, status?}) so filtered lists never share pages.
        enrollments: (organizationID, filterKey = 'all') => ['followUp', organizationID, 'enrollments', filterKey],
        enrollment: (organizationID, enrollmentID) => ['followUp', organizationID, 'enrollment', enrollmentID],
        // §25.9 — template-free care episodes. 'careEpisodes' covers the
        // dashboard/person scoped lists; 'careEpisode' is the detail route.
        careEpisodes: (organizationID, filterKey = 'all') => ['followUp', organizationID, 'careEpisodes', filterKey],
        careEpisode: (organizationID, careEpisodeID) => ['followUp', organizationID, 'careEpisode', careEpisodeID],
        // The close-care pending-work preview (spec §13.6/§25.9). Separate
        // from 'careEpisode' so a live episode refetch never races the
        // decision-time snapshot the closer is reviewing.
        careClosePreview: (organizationID, careEpisodeID) => ['followUp', organizationID, 'closeCarePreview', careEpisodeID],
    },
    milestones: {
        catalog: () => ['milestoneCatalog'],
        definitions: (organizationID, scope) => ['milestones', organizationID, 'definitions', scope],
        definition: (organizationID, milestoneID) => ['milestones', organizationID, 'definition', milestoneID],
        organizationHistory: (organizationID, accountID) => ['milestones', organizationID, accountID, 'organizationHistory'],
        // Viewer account is part of private/read-filtered keys — a different
        // viewer must never inherit another account's donation-filtered state.
        person: (organizationID, viewerAccountID, personID) => ['milestones', organizationID, viewerAccountID, 'person', personID],
        celebrations: (organizationID, accountID, personID) => ['milestones', organizationID, accountID, personID, 'celebrations'],
    },
}